import { useState } from 'react'
import { Key, Plus, Trash2, Copy, Check, AlertTriangle } from 'lucide-react'
import { Button, Input } from '@brix/ui'
import { useApiKeys } from '@/hooks/useApiKeys'
import type { ApiKey } from '@brix/types'

export function ApiKeysPage() {
  const { apiKeys, isLoading, createApiKey, isCreating, deleteApiKey } = useApiKeys()
  const [newKeyName, setNewKeyName] = useState('')
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleCreate = async () => {
    if (!newKeyName.trim()) return
    const result = await createApiKey({ name: newKeyName.trim(), scopes: ['pages', 'blog'] })
    setCreatedKey(result.rawKey)
    setNewKeyName('')
  }

  const handleCopy = async () => {
    if (!createdKey) return
    await navigator.clipboard.writeText(createdKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await deleteApiKey(id)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <Key className="h-6 w-6 text-violet-600" />
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
        </div>
        <p className="text-gray-500 text-sm">
          Generate API keys so AI agents and external tools can create pages, blocks, and blog posts on behalf of your
          workspace.
        </p>
      </div>

      {/* Newly created key — show once */}
      {createdKey && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm font-medium text-amber-800">
              Copy this key now — it will not be shown again.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-lg bg-white border border-amber-200 px-3 py-2 text-sm font-mono text-gray-800 select-all truncate">
              {createdKey}
            </code>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors shrink-0"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            onClick={() => setCreatedKey(null)}
            className="mt-2 text-xs text-amber-600 hover:underline"
          >
            I've saved it, dismiss
          </button>
        </div>
      )}

      {/* Create form */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Create new key</h2>
        <div className="flex gap-3">
          <Input
            placeholder="Key name (e.g. GPT-4 Agent, n8n Workflow)"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="flex-1"
          />
          <Button onClick={handleCreate} disabled={!newKeyName.trim() || isCreating} className="shrink-0">
            <Plus className="h-4 w-4 mr-1" />
            {isCreating ? 'Creating…' : 'Create'}
          </Button>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Keys have access to pages and blog posts. Scoped to your workspace.
        </p>
      </div>

      {/* Keys list */}
      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
        {isLoading ? (
          <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : apiKeys.length === 0 ? (
          <div className="py-10 text-center">
            <Key className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No API keys yet</p>
          </div>
        ) : (
          apiKeys.map((key: ApiKey) => (
            <div key={key.id} className="flex items-center justify-between px-5 py-4 gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{key.name}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <code className="text-xs font-mono text-gray-400">{key.keyPrefix}••••••••</code>
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-gray-400">
                    Created {new Date(key.createdAt).toLocaleDateString()}
                  </span>
                  {key.lastUsedAt && (
                    <>
                      <span className="text-xs text-gray-300">·</span>
                      <span className="text-xs text-gray-400">
                        Last used {new Date(key.lastUsedAt).toLocaleDateString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {key.scopes.map((scope) => (
                  <span
                    key={scope}
                    className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
                  >
                    {scope}
                  </span>
                ))}
                <button
                  onClick={() => handleDelete(key.id)}
                  disabled={deletingId === key.id}
                  className="ml-2 rounded-lg p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                  title="Revoke key"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* AI integration guide */}
      <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">How to use with AI agents</h2>
        <p className="text-xs text-gray-500 mb-3">
          Pass your key in the <code className="bg-white px-1 rounded border border-gray-200">X-Api-Key</code> header
          when calling the Brix AI API:
        </p>
        <pre className="text-xs font-mono bg-gray-900 text-green-400 rounded-lg p-4 overflow-x-auto">
{`# Create a landing page with blocks
curl -X POST https://<project>.supabase.co/functions/v1/ai-api/pages \\
  -H "X-Api-Key: brix_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "siteId": "<your-site-id>",
    "title": "About Us",
    "slug": "about",
    "publishImmediately": true,
    "blocks": [
      { "type": "hero", "props": { "headline": "We build the future", "alignment": "center" } },
      { "type": "text",  "props": { "content": "<p>Our story...</p>" } }
    ]
  }'`}
        </pre>
        <p className="mt-3 text-xs text-gray-400">
          Available endpoints: <code>GET /sites</code> · <code>POST /pages</code> ·{' '}
          <code>PUT /pages/:id/blocks</code> · <code>POST /pages/:id/publish</code> ·{' '}
          <code>POST /blog-posts</code> · <code>PUT /blog-posts/:id</code>
        </p>
      </div>
    </div>
  )
}
